import React, { useState, useEffect } from 'react';
import { Badge } from '../ui/badge';
import { Activity, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UserActivityWidget = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchActivities();
    // Refresh every 30 seconds
    const interval = setInterval(fetchActivities, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchActivities = async () => {
    try {
      const response = await axios.get(`${API}/director/user-activity`);
      // Sort by last activity
      const sorted = response.data.sort((a, b) => {
        if (!a.last_activity) return 1;
        if (!b.last_activity) return -1;
        return new Date(b.last_activity) - new Date(a.last_activity);
      });
      setActivities(sorted);
    } catch (error) {
      console.error('Error fetching user activity:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0';
    if (amount >= 1000) return `$${(amount / 1000).toFixed(0)}K`;
    return `$${amount.toLocaleString()}`;
  };

  const getRoleColor = (role) => {
    const colors = {
      'sales': 'border-l-blue-500',
      'call_center': 'border-l-purple-500',
      'sales_manager': 'border-l-green-500'
    };
    return colors[role] || 'border-l-gray-500';
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-2 max-h-72 overflow-y-auto">
      {activities.length > 0 ? (
        activities.map((activity, idx) => (
          <div key={idx} className={`p-2 border-l-4 ${getRoleColor(activity.role)} pl-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition`}>
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="font-semibold text-sm">{activity.user_name}</div>
                <div className="text-xs text-gray-600 mt-1">
                  {activity.deals_closed > 0 && (
                    <span className="mr-2">🎉 {activity.deals_closed} deal{activity.deals_closed > 1 ? 's' : ''}</span>
                  )}
                  {activity.estimates_created > 0 && (
                    <span className="mr-2">📄 {activity.estimates_created} estimate{activity.estimates_created > 1 ? 's' : ''}</span>
                  )}
                  {activity.tasks_completed > 0 && (
                    <span className="mr-2">✅ {activity.tasks_completed} task{activity.tasks_completed > 1 ? 's' : ''}</span>
                  )}
                </div>
                {activity.revenue_generated > 0 && (
                  <div className="text-xs font-semibold text-green-600 mt-1">
                    <TrendingUp className="h-3 w-3 inline mr-1" />
                    {formatCurrency(activity.revenue_generated)}
                  </div>
                )}
              </div>
              <div className="text-xs text-gray-500">
                {activity.last_activity
                  ? formatDistanceToNow(new Date(activity.last_activity), { addSuffix: true })
                  : 'No recent activity'
                }
              </div>
            </div>
          </div>
        ))
      ) : (
        <div className="text-center py-8 text-gray-500 border-2 border-dashed rounded-lg">
          <Activity className="h-8 w-8 mx-auto mb-2 text-gray-400" />
          <p className="text-sm">No recent activity</p>
        </div>
      )}
    </div>
  );
};

export default UserActivityWidget;